/**
 * Genie animation helpers — a surface "grows out of" the button that opened it
 * (transform-origin pinned to the trigger), and the trigger springs a "pop".
 * The Apple Mail compose-button feel.
 *
 * Shared by the `<Sheet>` bottom-sheet (via useBottomSheet) and the MuckaPanel
 * chat modal, which is hand-rolled rather than a `<Sheet>`.
 *
 * Note on direction: we only grow-IN from the trigger. Closing always leaves
 * DOWNWARD (a slide), because scale-collapsing a full-height surface back into a
 * trigger that sits above its bottom edge reads as a shrinking floating square
 * rather than a natural dismiss. Callers own their own close (slide / translateY).
 */

export const GENIE_IN_MS = 440;
export const GENIE_SCALE = 0.22; // collapsed size at the trigger
export const GENIE_IN_EASE = 'cubic-bezier(0.22, 1.2, 0.36, 1)'; // decelerate + slight overshoot

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** A quick spring "pop" on the trigger element as the surface hands off to / from it. */
export function popElement(el: HTMLElement | null | undefined): void {
  if (!el || prefersReducedMotion()) return;
  try {
    el.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.14)', offset: 0.4 },
        { transform: 'scale(1)' },
      ],
      { duration: 360, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
    );
  } catch {
    /* WAA unsupported — skip the pop */
  }
}

/** transform-origin (px, relative to `el`'s box) pointing at the trigger's centre. */
export function originForTrigger(el: HTMLElement, trigger: HTMLElement): string {
  const sr = el.getBoundingClientRect();
  const tr = trigger.getBoundingClientRect();
  const ox = tr.left + tr.width / 2 - sr.left;
  const oy = tr.top + tr.height / 2 - sr.top;
  return `${ox}px ${oy}px`;
}

interface GenieGrowOptions {
  durationMs?: number;
  scale?: number;
  easing?: string;
  /**
   * Fraction of the grow over which the surface fades in. Default: all of it.
   * Liquid Glass passes 0.15, so the surface is solid almost at once and reads
   * as growing out of the trigger rather than fading in over it.
   */
  fadeUntil?: number;
  /** Called once the grow settles (and inline styles are cleared). */
  onFinish?: () => void;
}

/**
 * Grow `el` out of `trigger`: pin the transform-origin to the trigger, paint the
 * collapsed (scaled-down, transparent) start state, then spring up to full size.
 * Reduced motion degrades to a plain fade. Returns the Animation (cancellable)
 * or null when there's nothing to animate.
 *
 * IMPORTANT: call this from a layout effect (before paint) so the collapsed
 * start state is the first painted frame — otherwise you get a flash of the
 * full surface. Disable any competing CSS transition/animation on `el` first.
 */
export function genieGrowIn(
  el: HTMLElement,
  trigger: HTMLElement | null | undefined,
  opts: GenieGrowOptions = {},
): Animation | null {
  if (!trigger) return null;
  const {
    durationMs = GENIE_IN_MS,
    scale = GENIE_SCALE,
    easing = GENIE_IN_EASE,
    fadeUntil,
    onFinish,
  } = opts;
  const reduce = prefersReducedMotion();

  el.style.transformOrigin = originForTrigger(el, trigger);
  el.style.opacity = '0';
  if (!reduce) el.style.transform = `scale(${scale})`;

  const clear = () => {
    el.style.opacity = '';
    el.style.transform = '';
    el.style.transformOrigin = '';
  };

  let anim: Animation;
  try {
    anim = el.animate(
      reduce
        ? [{ opacity: 0 }, { opacity: 1 }]
        : fadeUntil !== undefined
          ? [
              { transform: `scale(${scale})`, opacity: 0, offset: 0 },
              { opacity: 1, offset: fadeUntil },
              { transform: 'scale(1)', opacity: 1, offset: 1 },
            ]
          : [
              { transform: `scale(${scale})`, opacity: 0 },
              { transform: 'scale(1)', opacity: 1 },
            ],
      { duration: reduce ? 180 : durationMs, easing: reduce ? 'ease' : easing, fill: 'both' },
    );
  } catch {
    clear();
    onFinish?.();
    return null;
  }

  const done = () => {
    clear();
    try { anim.cancel(); } catch { /* already gone */ }
    onFinish?.();
  };
  anim.addEventListener('finish', done);
  return anim;
}
